// Service Worker Registration and Update Management

export interface ServiceWorkerRegistrationState {
  registration: ServiceWorkerRegistration | null;
  updateAvailable: boolean;
  offline: boolean;
}

let registrationState: ServiceWorkerRegistrationState = {
  registration: null,
  updateAvailable: false,
  offline: !navigator.onLine,
};

const listeners: Set<(state: ServiceWorkerRegistrationState) => void> = new Set();

// Notify all listeners of state changes
function notifyListeners() {
  listeners.forEach((listener) => listener(registrationState));
}

// Register service worker
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[PWA] Service workers are not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
    });

    console.log('[PWA] Service Worker registered:', registration);

    registrationState.registration = registration;
    notifyListeners();

    // Check for updates immediately
    await registration.update();

    // Listen for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      console.log('[PWA] New service worker found, installing...');

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New service worker is installed and waiting
          console.log('[PWA] New service worker installed and waiting');
          registrationState.updateAvailable = true;
          notifyListeners();
        }
      });
    });

    // Listen for controller change (service worker activated)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[PWA] Service worker controller changed, reloading...');
      window.location.reload();
    });

    return registration;
  } catch (error) {
    console.error('[PWA] Service Worker registration failed:', error);
    return null;
  }
}

// Unregister service worker (for development/testing)
export async function unregisterServiceWorker(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const unregistered = await registration.unregister();
    if (unregistered) {
      console.log('[PWA] Service Worker unregistered');
      registrationState.registration = null;
      registrationState.updateAvailable = false;
      notifyListeners();
    }
    return unregistered;
  } catch (error) {
    console.error('[PWA] Service Worker unregistration failed:', error);
    return false;
  }
}

// Update service worker (skip waiting and reload)
export async function updateServiceWorker(): Promise<void> {
  if (!registrationState.registration || !registrationState.registration.waiting) {
    return;
  }

  // Tell the service worker to skip waiting
  registrationState.registration.waiting.postMessage({ type: 'SKIP_WAITING' });

  // Wait a bit for the service worker to activate, then reload
  setTimeout(() => {
    window.location.reload();
  }, 100);
}

// Check for updates
export async function checkForUpdates(): Promise<void> {
  if (!registrationState.registration) {
    return;
  }

  try {
    await registrationState.registration.update();
  } catch (error) {
    console.error('[PWA] Failed to check for updates:', error);
  }
}

// Subscribe to state changes
export function subscribeToServiceWorkerState(
  listener: (state: ServiceWorkerRegistrationState) => void
): () => void {
  listeners.add(listener);
  // Immediately call with current state
  listener(registrationState);

  // Return unsubscribe function
  return () => {
    listeners.delete(listener);
  };
}

// Get current state
export function getServiceWorkerState(): ServiceWorkerRegistrationState {
  return { ...registrationState };
}

// Initialize offline detection
export function initializeOfflineDetection(): void {
  const updateOfflineStatus = () => {
    registrationState.offline = !navigator.onLine;
    notifyListeners();
  };

  window.addEventListener('online', updateOfflineStatus);
  window.addEventListener('offline', updateOfflineStatus);

  // Initial check
  updateOfflineStatus();
}
